// ============================================================================
// Sechs Kästchen statt eines Textfeldes.
//
//   ORIGINALTEXT MARVIN (09.09.2026)
//     „die abfrage zum 2FA machen wir mit nach dem passwort login, auf einer
//      extra seite, der code soll in 6 kästchen eingetragen werden, nicht in
//      einem textfeld."
//
// WARUM DIE REGELN HIER STEHEN UND NICHT IM BAUTEIL DANEBEN.
//
// Eine Datei mit JSX lässt sich mit `--experimental-strip-types` nicht laden;
// was nur dort stünde, wäre bloss über eine Textsuche zu erreichen - und
// **eine Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.** Ob aus
// „12" in Kästchen 3 wirklich „12" wird und die Schreibmarke auf Kästchen 5
// springt, muss man ausrechnen. Neunzehnte Datei dieser Art nach `namen.ts`,
// `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`,
// `kartenebene.ts`, `grenzpunkte.ts`, `handbuchmarken.ts`,
// `tabellenbeschriftung.ts`, `zahlwoerter.ts`, `bruchstellen.ts`,
// `anlasstext.ts`, `technikfelder.ts`, `grabenlaenge.ts`, `kartenstart.ts`,
// `dateiart.ts`, `fensterstapel.ts` und `ereignisquelle.ts`.
//
// WAS DIESE FELDER NICHT SIND: ein Weg für den Wiederherstellungscode. Der
// hat die Gestalt `ABCDE-FGHIJ`, trägt Buchstaben und ist elf Zeichen lang -
// sechs Ziffernkästchen wären dort eine Sackgasse. Er behält sein Textfeld.
// ============================================================================

/** So viele Kästchen, und so lang ist ein Code aus der App. */
export const STELLEN = 6;

/**
 * Was von einer Eingabe übrig bleibt.
 *
 * Ein Mensch tippt, ein Passwortspeicher fügt ein, ein Telefon schlägt den
 * Code aus einer SMS vor - und was dabei ankommt, trägt Leerzeichen,
 * Bindestriche und manchmal einen Buchstaben. **Was keine Ziffer ist, fällt
 * weg, statt das Kästchen zu sperren:** ein Feld, das eine eingefügte
 * Zeichenfolge stumm abweist, sieht aus wie ein kaputtes.
 */
export function nurZiffern(text: string, hoechstens = STELLEN): string {
  return (text ?? '').replace(/\D+/g, '').slice(0, hoechstens);
}

/** Der Stand: was steht in den Kästchen, und wo blinkt die Schreibmarke. */
export type Stand = { code: string; stelle: number };

function klemme(n: number): number {
  return Math.max(0, Math.min(STELLEN - 1, n));
}

/**
 * Eine Eingabe in Kästchen `stelle`.
 *
 * **SIE ÜBERSCHREIBT, SIE SCHIEBT NICHT.** Wer in Kästchen 3 zurückgeht und
 * eine andere Ziffer tippt, will die dritte Ziffer ändern und nicht den Rest
 * nach hinten schieben - alles andere überrascht beim zweiten Mal.
 *
 * **UND SIE NIMMT MEHR ALS EINE ZIFFER.** Genau das passiert beim Einfügen
 * und bei jedem Telefon, das den Code aus der Nachricht anbietet: dann kommen
 * sechs Zeichen in EINEM Kästchen an. Sie werden von dieser Stelle an
 * verteilt, und die Schreibmarke steht danach hinter der letzten.
 */
export function beimTippen(vorher: string, stelle: number, eingabe: string): Stand {
  const s = klemme(stelle);
  const neu = nurZiffern(eingabe, STELLEN - s);
  if (!neu) return { code: vorher, stelle: s };

  const zeichen = vorher.padEnd(STELLEN, ' ').split('');
  for (let i = 0; i < neu.length; i++) zeichen[s + i] = neu[i]!;

  return {
    code: zeichen.join('').trimEnd(),
    /* HINTER DIE LETZTE GESCHRIEBENE, aber nie über das letzte Kästchen
       hinaus: dort bleibt die Schreibmarke stehen, damit man die sechste
       Ziffer noch berichtigen kann. */
    stelle: klemme(s + neu.length),
  };
}

/**
 * Die Rücktaste.
 *
 * **STEHT IM KÄSTCHEN ETWAS, WIRD ES GELÖSCHT; IST ES LEER, GEHT ES EINS
 * ZURÜCK UND LÖSCHT DORT.** Das ist das Verhalten, das jeder von einem
 * einzelnen Textfeld kennt - und es ist der Grund, warum Kästchen sonst als
 * unbedienbar gelten: wer nur den Sprung baut, löscht nie etwas, und wer nur
 * löscht, kommt nie zurück.
 */
export function beimZurueck(vorher: string, stelle: number): Stand {
  const s = klemme(stelle);
  const zeichen = vorher.padEnd(STELLEN, ' ').split('');

  if (zeichen[s] !== ' ') {
    zeichen[s] = ' ';
    return { code: zeichen.join('').trimEnd(), stelle: s };
  }
  if (s === 0) return { code: vorher, stelle: 0 };

  zeichen[s - 1] = ' ';
  return { code: zeichen.join('').trimEnd(), stelle: s - 1 };
}

/** Die Pfeiltasten. Sie ändern nichts, sie gehen. */
export function beimGehen(vorher: string, stelle: number, richtung: -1 | 1): Stand {
  return { code: vorher, stelle: klemme(stelle + richtung) };
}

/**
 * Was in Kästchen `i` steht.
 *
 * Eine Lücke bleibt eine Lücke: `beimTippen` füllt beim Einfügen von hinten
 * her auf, und wer in Kästchen 4 anfängt, hat die ersten drei leer. Sie als
 * Null zu zeigen wäre eine Ziffer, die niemand getippt hat.
 */
export function zeichenAn(code: string, i: number): string {
  const z = (code ?? '')[i];
  return z && z !== ' ' ? z : '';
}

/** Sechs Ziffern, keine Lücke - erst dann lässt sich absenden. */
export function vollstaendig(code: string): boolean {
  return /^\d{6}$/.test(code ?? '');
}
